<script setup>
import { computed, ref } from "vue";
import { useAuthorStore } from "@/stores/useAuthorStore";
import DeleteConfirmModal from "@/components/Generics/DeleteConfirmModal.vue";

const emit = defineEmits(["close", "refresh", "notify"]);
const authorStore = useAuthorStore();

// Congela a contagem no momento em que o modal é criado/aberto
const frozenCount = ref(authorStore.selectedAuthorIds.length);
if (!frozenCount.value || frozenCount.value < 1) frozenCount.value = 1;

const messageTitle = computed(() => {
  if (frozenCount.value > 1) {
    return `Tem certeza que deseja excluir ${frozenCount.value} autores?`;
  }

  return "Tem certeza que deseja excluir este autor?";
});

const messageSubtitle = computed(() => {
  return (
    "Esta ação não pode ser desfeita.\n" +
    "Se algum autor estiver vinculado a Textos, a exclusão será bloqueada."
  );
});

const successMessage = computed(() => {
  if (frozenCount.value === 1) {
    return "Autor apagado com sucesso.";
  }

  return `${frozenCount.value} autores apagados com sucesso.`;
});

// ===============================
// Tratamento de erro específico (lote)
// ===============================
function buildBatchAuthorDeleteError(error) {
  const FOREIGN_KEY_ERROR_STRING =
    "Erro interno ao remover autores: Cannot delete or update a parent row: a foreign key constraint fails (`litterae`.`texts`, CONSTRAINT `fk_texts_authors1` FOREIGN KEY (`author_id`) REFERENCES `authors` (`author_id`))";

  const defaultMessage =
    "Não é possível excluir esses AUTORES porque existe TEXTO cadastrado vinculado a eles.";

  const raw = error?.response?.data?.message || error?.message || "";

  if (raw.trim() === FOREIGN_KEY_ERROR_STRING.trim()) {
    return (
      "Um ou mais autores selecionados estão vinculados a Textos e não podem ser removidos.\n" +
      "Para excluí-los, remova todos os textos associados a estes autores primeiro."
    );
  }

  return raw || defaultMessage;
}

async function onConfirmBatchDelete() {
  try {
    await authorStore.deleteSelectedAuthors();

    authorStore.shouldForcePageOne = true;
    authorStore.selectedAuthorIds = [];

    emit("notify", {
      type: "success",
      message: successMessage.value,
    });

    emit("refresh");
    emit("close");
  } catch (error) {
    const msg = buildBatchAuthorDeleteError(error);

    emit("notify", {
      type: "error",
      message: msg,
    });

    throw new Error(msg);
  }
}
</script>

<template>
  <DeleteConfirmModal
    :show="true"
    title="Apagar Autores em Lote"
    :messageTitle="messageTitle"
    :messageSubtitle="messageSubtitle"
    confirmText="Sim, apagar"
    cancelText="Cancelar"
    loadingText="Apagando..."
    :onConfirm="onConfirmBatchDelete"
    @close="emit('close')"
  />
</template>
